import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Contribution,
  ContributionFilters,
  PaginationParams,
  PaginatedResponse,
} from '../models/contribution.models';

@Injectable({
  providedIn: 'root',
})
export class ContributionAdminService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/admin/contributions`;

  /**
   * List all contributions with filters and pagination
   *
   * @param filters - Optional filters (search, status, paymentMethod, date range)
   * @param pagination - Pagination params (page, limit)
   * @returns Paginated response with contributions
   */
  listContributions(
    filters: ContributionFilters = {},
    pagination: PaginationParams = { page: 1, limit: 20 },
  ): Observable<PaginatedResponse<Contribution>> {
    let params = new HttpParams()
      .set('page', pagination.page.toString())
      .set('limit', pagination.limit.toString());

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.status) {
      params = params.set('status', filters.status);
    }

    if (filters.paymentMethod) {
      params = params.set('paymentMethod', filters.paymentMethod);
    }

    if (filters.startDate) {
      // Normalize to start of day in UTC to avoid timezone issues
      const startOfDay = new Date(filters.startDate);
      startOfDay.setUTCHours(0, 0, 0, 0);
      params = params.set('startDate', startOfDay.toISOString());
    }

    if (filters.endDate) {
      // Normalize to end of day in UTC to include the full day
      const endOfDay = new Date(filters.endDate);
      endOfDay.setUTCHours(23, 59, 59, 999);
      params = params.set('endDate', endOfDay.toISOString());
    }

    return this.http.get<PaginatedResponse<Contribution>>(this.apiUrl, {
      params,
    });
  }

  /**
   * Get contribution by ID (admin access)
   *
   * @param id - Contribution UUID
   * @returns Contribution with all details
   */
  getContribution(id: string): Observable<{ data: Contribution }> {
    return this.http.get<{ data: Contribution }>(`${this.apiUrl}/${id}`);
  }

  /**
   * Update admin notes for a contribution
   *
   * @param id - Contribution UUID
   * @param notes - New notes to append
   * @returns Updated contribution
   */
  updateAdminNotes(
    id: string,
    notes: string,
  ): Observable<{ data: Contribution; message: string }> {
    return this.http.patch<{ data: Contribution; message: string }>(
      `${this.apiUrl}/${id}/notes`,
      { notes },
    );
  }

  /**
   * Mark contribution as paid (bank transfer)
   *
   * @param id - Contribution UUID
   * @param paidAt - Payment date (optional)
   * @param notes - Admin notes about payment
   * @returns Updated contribution
   */
  markAsPaid(
    id: string,
    notes: string,
    paidAt?: Date,
  ): Observable<{ data: Contribution; message: string }> {
    return this.http.post<{ data: Contribution; message: string }>(
      `${environment.apiUrl}/admin/contributions/${id}/mark-paid`,
      {
        notes,
        paidAt: paidAt?.toISOString(),
      },
    );
  }

  /**
   * Get certificate download URL for a contribution
   * Opens the certificate in a new tab (redirects to signed S3 URL)
   *
   * @param id - Contribution UUID
   * @returns The URL to download the certificate
   */
  getCertificateDownloadUrl(id: string): string {
    return `${environment.apiUrl}/admin/contributions/${id}/certificate`;
  }

  /**
   * Get invoice download URL for a contribution
   * Opens the invoice in a new tab (redirects to signed S3 URL)
   *
   * @param id - Contribution UUID
   * @returns The URL to download the invoice
   */
  getInvoiceDownloadUrl(id: string): string {
    return `${environment.apiUrl}/admin/contributions/${id}/invoice`;
  }

  /**
   * Retry Odoo sync for a failed contribution
   *
   * @param id - Contribution UUID
   * @returns Success message
   */
  retryOdooSync(id: string): Observable<{ message: string; contributionId: string }> {
    return this.http.post<{ message: string; contributionId: string }>(
      `${environment.apiUrl}/admin/odoo/retry-sync/${id}`,
      {},
    );
  }
}
